/**
 * API client for Action Summary endpoints.
 *
 * Provides type-safe access to AI-generated daily action summaries
 * with prioritized tasks grouped by signal type and urgency.
 */

import { clientInstance } from '../utils/http-client'
import { baseAPICall } from './baseAPICall'
import type {
  ActionClickStatus,
  ActionClickUpdateStatus,
  ActionTypeEnum,
  EnrichedAction,
  SentimentType,
  SignalType,
} from './recommendations_contracts'

export interface InactiveDeal {
  deal_id: number
  identifier: string
  name: string
  contact_company_name: string
  sales_stage: string
  days_since_modified: number
  days_in_stage: number
  inactive_reason: 'no_activity' | 'stuck_in_stage' | 'both'
}

export interface EmailNeedingReply {
  email_id: number
  subject: string
  from_name: string
  from_email: string
  days_since_received: number
  related_contact_ids: number[]
  contact_company_id: number | null
}

export interface SignalCounts {
  unreplied_email: number
  inactive_deal: number
  /** Total signals detected (before limiting for synthesis) */
  total: number
  /** Number of signals sent to AI for synthesis (may be less than total) */
  synthesized: number
  /** True if signals were truncated due to limits */
  truncated: boolean
}

export interface ActionSummaryStructuredData {
  enriched_actions: EnrichedAction[]
  // Signal-based grouping
  actions_by_signal: {
    unreplied_email?: EnrichedAction[]
    inactive_deal?: EnrichedAction[]
  }
  signal_counts?: SignalCounts
  // Supporting data for debugging/display
  inactive_deals: InactiveDeal[]
  emails_needing_reply: EmailNeedingReply[]
  // Dedup: count of snoozed signals for this user
  snoozed_count?: number
}

/** Represents a user's click/sentiment on an action */
export interface UserClick {
  click_id: number
  deal_id: number | null
  signal_type: SignalType
  action_type: ActionTypeEnum
  sentiment: SentimentType | null
  status: ActionClickStatus
  signal_fingerprint?: string | null
}

export interface ActionSummaryData {
  /** ID of the summary record (null when no summary exists) */
  id: number | null
  user_email?: string
  user_name?: string
  created: string | null
  /** Status is null when no summary has been generated yet */
  status: 'GENERATING' | 'COMPLETED' | 'FAILED' | null
  summary_text: string
  summary_data: ActionSummaryStructuredData
  error_message?: string
  generation_duration_seconds?: number
  /** User clicks/sentiment for actions in this summary */
  user_clicks?: UserClick[]
}

// Homepage Stats Types (Tier 1 Metrics)
export interface StageCount {
  stage_id: number
  stage_name: string
  count: number
  total_value: number
}

export interface HomepageStats {
  // Pipeline summary
  open_deals_count: number
  pipeline_total_value: number
  won_deals_count: number
  lost_deals_count: number

  // Deals by stage
  deals_by_stage: StageCount[]
}

// Re-export shared contracts for convenience
export type {
  EnrichedAction,
  SignalType,
  ActionTypeEnum,
  SentimentType,
} from './recommendations_contracts'

/**
 * Fetches the latest action summary for the authenticated user.
 *
 * @param userId - Optional user ID (superusers only) to view another user's summary
 * @returns Promise resolving to the user's most recent action summary
 * @throws Error with 404 status if no summary exists yet
 * @throws Error with 403 status if enable_homepage feature flag not enabled
 */
export async function getLatestActionSummary(userId?: number) {
  const params = userId ? { user_id: userId } : {}
  return baseAPICall<ActionSummaryData>(
    () => clientInstance.get('recommendations/latest/', { params }),
    { disableNotifyError: true }
  )
}

/**
 * Triggers generation of a new action summary for the authenticated user.
 *
 * Queues a background task to generate the summary. The summary will be
 * available via getLatestActionSummary() once generation completes.
 *
 * @param userId - Optional user ID (superusers only) to generate for another user
 * @param force - If true, regenerate even if a summary already exists for today
 * @returns Promise resolving to task information
 * @throws Error with 403 status if enable_homepage feature flag not enabled
 */
export async function generateActionSummary(userId?: number) {
  const params: Record<string, string | number> = {}
  if (userId) params.user_id = userId
  return baseAPICall<{
    detail: string
    task_id: string
    user_id?: number
    user_email?: string
  }>(() => clientInstance.post('recommendations/generate/', null, { params }), {
    disableNotifyError: false,
  })
}

/**
 * Fetches real-time homepage statistics for the authenticated user.
 *
 * Returns Tier 1 metrics including:
 * - Pipeline summary (open deals, total value, won/lost counts)
 * - Deals by stage breakdown
 * - Alert counts (stale deals, emails needing reply)
 * - Activity counts (reminders, recent emails/notes)
 *
 * @param userId - Optional user ID (superusers only) to view another user's stats
 * @returns Promise resolving to the user's homepage statistics
 * @throws Error with 403 status if enable_homepage feature flag not enabled
 */
export async function getHomepageStats(userId?: number) {
  const params = userId ? { user_id: userId } : {}
  return baseAPICall<HomepageStats>(
    () => clientInstance.get('recommendations/stats/', { params }),
    { disableNotifyError: true }
  )
}

export interface RecordActionClickParams {
  action_summary_id: number
  signal_type: SignalType
  action_type: ActionTypeEnum
  deal_id?: number
  contact_company_id?: number
  sentiment?: SentimentType | null
  action_text?: string
  recommendation_text?: string
  status?: ActionClickStatus
  signal_fingerprint?: string | null
}

// Performance Stats Types (Personal Performance Dashboard)
export interface PerformancePeriodData {
  period: string // YYYY-MM for monthly, YYYY-Www for weekly
  period_label: string // Human-readable label (e.g., "Jan 2025")
  deals_opened: number
  deals_closed: number
  deals_won: number
  deals_lost: number
  emails_sent: number // Outbound emails from user's connected account
  emails_received: number // Inbound emails to user's connected account
  win_rate: number | null // Percentage (0-100), null if no closed deals
  value_opened: number // Annual value of deals opened (1yr revenue)
  value_won: number // Annual value of deals won (1yr revenue)
}

export interface PerformanceTotalsData {
  deals_opened: number
  deals_won: number
  deals_lost: number
  emails_sent: number
  emails_received: number
  overall_win_rate: number | null
  value_opened: number // Total annual value of deals opened
  value_won: number // Total annual value of deals won
}

/** Supported time range presets for performance data. */
export type PerformanceRange = '3m' | '6m' | '12m' | 'ytd' | 'all'

/** Human-readable labels for each range option. */
export const PERFORMANCE_RANGE_LABELS: Record<PerformanceRange, string> = {
  '3m': 'Last 3 Months',
  '6m': 'Last 6 Months',
  '12m': 'Last 12 Months',
  ytd: 'Year to Date',
  all: 'All Time',
}

export interface PerformanceRangeInfo {
  range: PerformanceRange
  range_label: string
  start_date: string | null // ISO date string, null for "all"
  end_date: string // ISO date string
}

export interface PerformanceStats {
  period_type: 'monthly' | 'weekly'
  periods: PerformancePeriodData[]
  totals: PerformanceTotalsData
  range_info: PerformanceRangeInfo
}

export interface RecordActionClickResponse {
  detail: string
  click_id: number
}

/**
 * Records when a user clicks on a recommended action.
 *
 * Used to track engagement with different signal types and tune
 * recommendations based on what users find valuable.
 *
 * @param params - Click tracking parameters
 * @returns Promise resolving to click confirmation
 */
export async function recordActionClick(params: RecordActionClickParams) {
  return baseAPICall<RecordActionClickResponse>(
    () => clientInstance.post('recommendations/click/', params),
    { disableNotifyError: true }
  )
}

export interface UpdateSentimentParams {
  click_id: number
  sentiment: SentimentType | null
}

export interface UpdateSentimentResponse {
  detail: string
  click_id: number
  sentiment: SentimentType | null
}

/**
 * Updates sentiment (thumbs up/down) on an existing action click.
 *
 * Allows users to provide feedback separate from the initial click.
 * "Did not click" ≠ "not helpful" - we need explicit feedback.
 *
 * @param params - Sentiment update parameters
 * @returns Promise resolving to sentiment update confirmation
 */
export async function updateActionClickSentiment(
  params: UpdateSentimentParams
) {
  return baseAPICall<UpdateSentimentResponse>(
    () =>
      clientInstance.patch(
        `recommendations/click/${params.click_id}/sentiment/`,
        {
          sentiment: params.sentiment,
        }
      ),
    { disableNotifyError: true }
  )
}

export interface UpdateStatusParams {
  click_id: number
  status: ActionClickUpdateStatus
}

export interface UpdateStatusResponse {
  detail: string
  click_id: number
  status: ActionClickUpdateStatus | ActionClickStatus
}

/**
 * Updates status on an existing action click.
 *
 * Allows users to mark actions as done or dismissed.
 *
 * @param params - Status update parameters
 * @returns Promise resolving to status update confirmation
 */
export async function updateActionClickStatus(params: UpdateStatusParams) {
  return baseAPICall<UpdateStatusResponse>(
    () =>
      clientInstance.patch(`recommendations/click/${params.click_id}/status/`, {
        status: params.status,
      }),
    { disableNotifyError: true }
  )
}

// Pipeline Value Stats Types (Pipeline Health Over Time)
export interface PipelinePeriodData {
  period: string // YYYY-MM for monthly, YYYY-Www for weekly
  period_label: string // Human-readable label (e.g., "Jan 2025")
  deal_count: number
  unweighted_value: number
  weighted_value: number
}

export interface PipelineTotalsData {
  current_deal_count: number
  current_unweighted_value: number
  current_weighted_value: number
}

export interface PipelineValueStats {
  period_type: 'monthly' | 'weekly'
  periods: PipelinePeriodData[]
  totals: PipelineTotalsData
  range_info: PerformanceRangeInfo
}

export interface GetPipelineValueStatsParams {
  userId?: number
  periodType?: 'monthly' | 'weekly'
  range?: PerformanceRange
}

/**
 * Fetches historical pipeline value statistics for the authenticated user.
 *
 * Returns time-series data for:
 * - Deal count in pipeline at each period boundary
 * - Unweighted pipeline value (sum of annual_revenue)
 * - Weighted pipeline value (sum of annual_revenue * stage win_rate)
 *
 * @param params - Query parameters for filtering and time range
 * @returns Promise resolving to pipeline value statistics
 * @throws Error with 403 status if enable_homepage feature flag not enabled
 */
export async function getPipelineValueStats(
  params: GetPipelineValueStatsParams = {}
) {
  const queryParams: Record<string, string | number> = {}
  if (params.userId) queryParams.user_id = params.userId
  if (params.periodType) queryParams.period_type = params.periodType
  if (params.range) queryParams.range = params.range

  return baseAPICall<PipelineValueStats>(
    () =>
      clientInstance.get('recommendations/pipeline-value/', {
        params: queryParams,
      }),
    { disableNotifyError: true }
  )
}

export interface GetPerformanceStatsParams {
  userId?: number
  periodType?: 'monthly' | 'weekly'
  range?: PerformanceRange
}

/**
 * Fetches historical performance statistics for the authenticated user.
 *
 * Returns time-series data for:
 * - Deals opened per period
 * - Deals closed/won/lost per period
 * - Emails sent per period
 * - Win rate per period
 *
 * @param params - Query parameters for filtering and time range
 * @returns Promise resolving to performance statistics
 * @throws Error with 403 status if enable_homepage feature flag not enabled
 */
export async function getPerformanceStats(
  params: GetPerformanceStatsParams = {}
) {
  const queryParams: Record<string, string | number> = {}
  if (params.userId) queryParams.user_id = params.userId
  if (params.periodType) queryParams.period_type = params.periodType
  if (params.range) queryParams.range = params.range

  return baseAPICall<PerformanceStats>(
    () =>
      clientInstance.get('recommendations/performance/', {
        params: queryParams,
      }),
    { disableNotifyError: true }
  )
}
